import React, {Fragment, MouseEvent} from 'react';
import PropTypes, {InferProps} from 'prop-types';
import {Box, Link as MuiLink, Typography} from '@material-ui/core';
import {BulletStyles} from 'assets/tss/layouts/components';
import {BulletItem} from 'commons/models';
import {EfeBulletList, EfeHelpIcon, EfeModal, EfeTooltip} from 'components';
import {Utils} from 'commons';

function EfeBullet(props: InferProps<typeof EfeBullet.propTypes>) {
    const {item, handleAction, handleHeaderAction, secondaryLink} = props;
    const [open, setOpen] = React.useState<boolean>(false);
    const classes = BulletStyles();
    return (
        <div>
            {item.sections.map((s) => {
                let props: Record<string, unknown>;
                let str = s.value;
                const title: string = s.title ? s.title : '';
                const bulletRef = React.useRef<HTMLSpanElement>(null);

                props = {display: 'inline'};
                const styles = ['strong', 'help', 'caption'];

                if (typeof s.value === 'string') {
                    // strong & help apply for string only
                    if (styles.includes(s.style)) {
                        if (s.style === 'help') {
                            // & bullet style is help
                            props = {...props, className: classes.help};
                        } else if (s.style === 'strong') {
                            // & bullet style is strong
                            str = <strong>{s.value}</strong>;
                        } else if (s.style === 'caption') {
                            // & bullet style is caption
                            props = {...props, variant: 'caption'};
                        }
                    }
                    str = s.value;
                }

                // tooltip need a text
                if (s.style === 'tooltip' && s.text) {
                    const rows = s.text.split('|');
                    const bulls: BulletItem[] = rows.map((row, i) => {
                        return {
                            item: i,
                            sections: [
                                {
                                    value: row,
                                    style: 'caption',
                                    order: 0,
                                },
                            ],
                        };
                    });
                    const sect = {
                        desc: s.value as string,
                        items: bulls,
                    };
                    const bullets = (
                        <EfeBulletList
                            key={Utils.functions.Generate.uniqueID()}
                            bullets={[sect]}
                            style={'caption'}
                        />
                    );
                    const tooltipTitle = rows.length > 1 ? bullets : rows[0];
                    if (s.variant === 'help') {
                        return (
                            <EfeHelpIcon
                                key={Utils.functions.Generate.uniqueID()}
                                title={tooltipTitle}
                            />
                        );
                    } else if (s.variant === 'link') {
                        return (
                            <EfeTooltip
                                title={tooltipTitle}
                                key={Utils.functions.Generate.uniqueID()}>
                                <MuiLink
                                    variant='body1'
                                    color='primary'
                                    className={secondaryLink ? classes.secondaryLink : undefined}
                                    underline='always'
                                    ref={bulletRef}>
                                    {str}
                                </MuiLink>
                            </EfeTooltip>
                        );
                    }
                }

                if (s.style === 'modal') {
                    if (s.text) {
                        const handleOpenClose = (event: MouseEvent<HTMLAnchorElement>) => {
                            event.preventDefault();
                            setOpen(!open);
                        };
                        const onAction = (action: string) => {
                            if (handleAction) {
                                handleAction({action: action});
                            }
                            setOpen((prevState) => !prevState);
                        };
                        const onHeaderAction = (action: string) => {
                            if (handleHeaderAction) {
                                handleHeaderAction(action);
                            }
                        };
                        const rows = s.text.split('|');
                        const bulletsData = rows.map((row) => {
                            return (
                                <Box key={Utils.functions.Generate.uniqueID()} mb={3}>
                                    <Typography
                                        variant={row.startsWith('<b1>') ? 'body1' : 'body2'}>
                                        {row.replace('<b1>', '')}
                                    </Typography>
                                </Box>
                            );
                        });

                        return (
                            <Fragment key={Utils.functions.Generate.uniqueID()}>
                                <MuiLink
                                    onClick={handleOpenClose}
                                    variant='body1'
                                    color='primary'
                                    underline='always'
                                    className={secondaryLink ? classes.secondaryLink : classes.link}
                                    ref={bulletRef}>
                                    {str}
                                </MuiLink>
                                <EfeModal
                                    open={open}
                                    title={title}
                                    headerActions={s.headerActions}
                                    reactElement={bulletsData}
                                    handleAction={onAction}
                                    handleHeaderAction={onHeaderAction}
                                />
                            </Fragment>
                        );
                    }
                    /**/
                }

                if (s.style === 'inline' && s.text) {
                    const onHeaderAction = (action: string) => {
                        if (handleHeaderAction) {
                            handleHeaderAction(action);
                        }
                    };
                    return (
                        <Fragment key={Utils.functions.Generate.uniqueID()}>
                            <MuiLink
                                onClick={() => {
                                    onHeaderAction(
                                        s.headerActions && s.headerActions[0]
                                            ? s.headerActions[0]?.id
                                            : ''
                                    );
                                }}
                                variant='body1'
                                color='primary'
                                underline='always'
                                className={secondaryLink ? classes.secondaryLink : classes.link}
                                ref={bulletRef}>
                                {str}
                            </MuiLink>
                        </Fragment>
                    );
                }

                // default returns a string
                return (
                    <Typography key={Utils.functions.Generate.uniqueID()} {...props}>
                        {str}
                    </Typography>
                );
            })}
        </div>
    );
}

EfeBullet.propTypes = {
    handleAction: PropTypes.func,
    handleHeaderAction: PropTypes.func,
    secondaryLink: PropTypes.bool,
    item: PropTypes.shape({
        item: PropTypes.number.isRequired,
        sections: PropTypes.arrayOf(
            PropTypes.shape({
                /** bullet value */
                value: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.node])
                    .isRequired,
                headerActions: PropTypes.arrayOf(
                    PropTypes.shape({
                        id: PropTypes.string.isRequired,
                        icon: PropTypes.node.isRequired,
                    })
                ),
                title: PropTypes.string,
                /** bullet style */
                style: PropTypes.oneOf([
                    'help',
                    'body',
                    'strong',
                    'tooltip',
                    'caption',
                    'modal',
                    'inline',
                ]).isRequired,
                /** bullet order */
                order: PropTypes.number.isRequired,
                /** tooltip text */
                text: PropTypes.string,
                /** tooltip variant */
                variant: PropTypes.oneOf(['help', 'link']),
            }).isRequired
        ).isRequired,
    }).isRequired,
};

export default EfeBullet;
